/**
 * Focus Trap Mixin for Modal Components
 *
 * Implements WCAG 2.1.2 No Keyboard Trap and 2.4.3 Focus Order requirements:
 * - Moves focus to modal when opened (optional, controlled by focusTrapAutoFocus)
 * - Traps focus within modal (Tab/Shift+Tab cycle)
 * - Closes modal on ESC key press
 * - Restores focus to trigger element when closed
 *
 * Usage:
 * 1. Import and add to mixins array
 * 2. Set `focusTrapEnabled` data property to control when trap is active
 * 3. Ensure modal container has ref="focusTrapContainer"
 * 4. Optionally implement `onFocusTrapEscape()` method for custom close behavior
 * 5. Optionally set `focusTrapAutoFocus: false` to prevent auto-focus on activation
 *
 * @example
 * import focusTrapMixin from '_shared_/js/mixins/focusTrapMixin';
 *
 * export default {
 *     mixins: [focusTrapMixin],
 *     data() {
 *         return {
 *             focusTrapEnabled: false,
 *             // Optional: set to false to prevent auto-focus when trap activates
 *             // focusTrapAutoFocus: false,
 *         };
 *     },
 *     methods: {
 *         openModal() {
 *             this.focusTrapEnabled = true;
 *         },
 *         closeModal() {
 *             this.focusTrapEnabled = false;
 *         },
 *         // Optional: custom close behavior for ESC key
 *         onFocusTrapEscape() {
 *             this.closeModal();
 *         },
 *     },
 * };
 */

const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(', ');

export default {
    data() {
        return {
            _focusTrap_previousActiveElement: null,
        };
    },
    watch: {
        focusTrapEnabled: {
            immediate: false,
            handler(isEnabled) {
                if (isEnabled) {
                    this._focusTrap_activate();
                } else {
                    this._focusTrap_deactivate();
                }
            },
        },
    },
    beforeDestroy() {
        this._focusTrap_deactivate();
    },
    methods: {
        /**
         * Activates the focus trap
         */
        _focusTrap_activate() {
            // Store the currently focused element to restore later
            this._focusTrap_previousActiveElement = document.activeElement;

            // Wait for DOM to update, then set up the trap
            this.$nextTick(() => {
                // Only auto-focus if focusTrapAutoFocus is not explicitly false
                if (this.focusTrapAutoFocus !== false) {
                    this._focusTrap_focusFirstElement();
                }
                document.addEventListener('keydown', this._focusTrap_handleKeyDown);
            });
        },

        /**
         * Deactivates the focus trap and restores focus
         */
        _focusTrap_deactivate() {
            document.removeEventListener('keydown', this._focusTrap_handleKeyDown);

            // Restore focus to the element that opened the modal
            if (this._focusTrap_previousActiveElement && this._focusTrap_previousActiveElement.focus) {
                this.$nextTick(() => {
                    this._focusTrap_previousActiveElement.focus();
                    this._focusTrap_previousActiveElement = null;
                });
            }
        },

        /**
         * Gets the modal container element
         * @returns {HTMLElement|null}
         */
        _focusTrap_getContainer() {
            return this.$refs.focusTrapContainer || null;
        },

        /**
         * Gets all focusable elements within the modal
         * @returns {HTMLElement[]}
         */
        _focusTrap_getFocusableElements() {
            const container = this._focusTrap_getContainer();
            if (!container) return [];

            const elements = container.querySelectorAll(FOCUSABLE_SELECTORS);
            return Array.from(elements).filter(el => {
                // Filter out elements that are not visible
                return el.offsetParent !== null && !el.hasAttribute('inert');
            });
        },

        /**
         * Focuses the first focusable element in the modal
         */
        _focusTrap_focusFirstElement() {
            const container = this._focusTrap_getContainer();
            if (!container) return;

            const focusableElements = this._focusTrap_getFocusableElements();

            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            } else {
                // If no focusable elements, focus the container itself
                container.setAttribute('tabindex', '-1');
                container.focus();
            }
        },

        /**
         * Handles keydown events for focus trapping and ESC to close
         * @param {KeyboardEvent} event
         */
        _focusTrap_handleKeyDown(event) {
            // Handle ESC key to close modal
            if (event.key === 'Escape') {
                event.preventDefault();
                this._focusTrap_handleEscape();
                return;
            }

            // Handle Tab key for focus cycling
            if (event.key !== 'Tab') return;

            const focusableElements = this._focusTrap_getFocusableElements();
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey) {
                // Shift + Tab: going backwards
                if (document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab: going forwards
                if (document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        },

        /**
         * Handles ESC key press to close the modal
         * Components can override onFocusTrapEscape() for custom close behavior
         */
        _focusTrap_handleEscape() {
            if (typeof this.onFocusTrapEscape === 'function') {
                this.onFocusTrapEscape();
            } else {
                this.focusTrapEnabled = false;
            }
        },
    },
};
